import React from 'react';
import { ExternalLink, Globe, FileText, Building } from 'lucide-react';

const ProductsSection = () => {
  const products = [
    {
      name: "Scenarionaut.com",
      description: "Automated, customizable training and evaluation platform for educators and trainers, with a current prototype geared towards high-school biology curriculum.",
      url: "https://www.scenarionaut.com/",
      status: "Live",
      category: "Education",
      logo: "imgs/scenarionaut-logo.png",
      features: ["Automated Training", "Customizable Curriculum", "Biology Focus", "Educator Tools"]
    },
    {
      name: "PatentLoop.com",
      description: "ChatGPT-like platform for natural language querying and generation for patent and technical documents.",
      url: "https://patentloop.com/",
      status: "Live",
      category: "Legal Tech",
      logo: "imgs/patentloop-icon.png",
      features: ["Natural Language Query", "Patent Analysis", "Technical Documents", "AI-Powered Search"],
      beta: true
    },
    {
      name: "ForGen AI Enterprise",
      description: "Agentic tooling platform for creating new tools, pipelines, agents, and agentic workflows.",
      url: "https://forgen.ai/",
      status: "Coming Soon",
      category: "AI / Agents",
      logo: "icon_forgen_512.png",
      features: ["Agentic Framework", "Agentic MCP (AMCP)", "Natural Language Query", "Document Analysis"],
    },
    {
      name: "Trademarks.chat",
      description: "ChatGPT-like platform for natural language querying and generation for preparing and analyzing US trademark applications and registrations.",
      url: "#",
      status: "Coming Soon",
      category: "Legal Tech",
      logo: (
        <div className="w-full h-full bg-gradient-to-br from-red-600 to-black rounded-lg flex items-center justify-center">
          <span className="text-white font-bold text-4xl">™</span>
        </div>
      ),
      features: ["Trademark Search", "Application Prep", "US Registration", "Natural Language Interface"]
    }
  ];

  const getStatusBadge = (status) => {
    if (status === "Live") {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
          Live
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <div className="w-2 h-2 bg-amber-400 rounded-full mr-1"></div>
          Coming Soon
        </span>
      );
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Education":
        return <Building className="w-5 h-5 text-red-500" />;
      case "Legal Tech":
        return <FileText className="w-5 h-5 text-green-700" />;
      default:
        return <Globe className="w-5 h-5 text-gray-500" />;
    }
  }

  const badgeColors = [
    'bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700',
    'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
    'bg-blue-100 text-blue-700 hover:bg-blue-200',
    'bg-purple-100 text-purple-700 hover:bg-purple-200',
  ]

  return (
    <section id="products" className="relative py-24 bg-gradient-to-br from-gray-900 via-black to-black z-10">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Products We <span className={`bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent`}>Power</span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Real-world applications of our AI expertise, delivering domain-specific solutions that empower professionals across industries.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {products.map((product, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 hover:border-red-500/30 hover:shadow-xl"
              onClick={() => {
                window.open(product.url, '_blank', 'noopener,noreferrer')
              }}
            >

              {/* Background gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-100/30 via-orange-100/30 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

              {/* Content */}
              <div className="relative p-6">
                {/* Logo Section - Large on Hover */}
                <div className="relative w-full h-40 sm:h-48 md:h-56 mb-6 overflow-hidden rounded-t-xl bg-black flex items-center justify-center">
                  {/* Optional overlays */}
                  <div className="absolute inset-0 bg-black/30 z-0" />
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-pink-500/10 to-purple-500/10 blur-2xl z-0" />

                  {/* Logo (slightly zoomed out by scale) */}
                  {typeof product.logo === 'string' ? (
                    <div className="relative z-10 w-full h-full scale-90">
                      <img
                        src={product.logo}
                        alt={`${product.name} logo`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="relative z-10 w-full h-full scale-125 flex items-center justify-center">
                      {/* Special styling just for TM */}
                      <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-gradient-to-br from-red-600 to-black rounded-xl flex items-center justify-center">
                        <span className="text-white font-bold text-6xl md:text-7xl leading-none">™</span>
                      </div>
                    </div>
                  )}

                </div>

                {/* Website Name - Prominent on Hover */}
                <div className="text-center mb-4">
                  <h3 className="text-xl group-hover:text-2xl font-bold text-red-200 group-hover:text-red-600 transition-all duration-300 mb-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-center space-x-2 flex-wrap">
                    {getCategoryIcon(product.category)}
                    <span className="text-sm text-gray-500">{product.category}</span>
                    {product.beta && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Beta
                      </span>
                    )}
                    {getStatusBadge(product.status)}
                  </div>
                </div>

                {/* Description */}
                <p className="text-white/70 mb-4 leading-relaxed text-center group-hover:text-base transition-all duration-300">
                  {product.description}
                </p>

                {/* Features */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {product.features.map((feature, featureIndex) => (
                      <span
                        key={featureIndex}
                        className={`px-3 py-1 text-xs rounded-full font-medium transition-all duration-300 ${badgeColors[featureIndex % badgeColors.length]}`}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex flex-col items-center space-y-3">
                  {product.status === "Live" ? (
                    <button
                      className="inline-flex items-center px-6 py-3 group-hover:px-8 group-hover:py-4 bg-gradient-to-r from-red-600 to-black text-white font-semibold rounded-lg hover:from-red-700 hover:to-gray-900 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                    >
                      <span>Visit Platform</span>
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </button>
                  ) : (
                    <button
                      disabled
                      className="inline-flex items-center px-6 py-3 group-hover:px-8 group-hover:py-4 bg-gray-300 text-gray-500 font-semibold rounded-lg cursor-not-allowed transition-all duration-300"
                    >
                      <span>Coming Soon</span>
                    </button>
                  )}

                  {/* Powered by ForGen AI badge */}
                  <div className="text-xs text-gray-400 font-medium">
                    Powered by ForGen AI
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
              </div>

              {/* Hover border glow */}
              <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-red-300 transition-all duration-300"></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 p-8 bg-gradient-to-r from-red-500 via-pink-500 to-purple-600 rounded-2xl shadow-xl">
          <h3 className="text-2xl font-bold text-white mb-2">
            Ready to Power Your Next Product?
          </h3>
          <p className="text-white/80 mb-4 max-w-2xl mx-auto">
            From concept to deployment, we help you build AI-powered solutions that solve real problems for real users.
          </p>
          <a
            href="mailto:info@forgen.ai"
            className="inline-flex items-center px-6 py-3 bg-white text-red-600 font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <span>Start Your Project</span>
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;